/**
 * Full Requirements Review - Shows ALL requirements from all domains
 * Based on EquipmentSelectionTab logic but displays complete requirements output
 */

import React, { useState, useEffect, useMemo } from 'react';
import { 
  Package, AlertCircle, FileText, Book, Award, Shield, 
  CheckCircle, XCircle, AlertTriangle, Download, Eye,
  Users, Wrench, Activity, Clipboard, Database
} from 'lucide-react';
import { getHardcodedVesselRequirements } from '../../../../config/hardcodedVesselRequirements';
import { useVessel } from '../../../../contexts/VesselContext';
import { REQUIREMENT_CATEGORIES } from './utils/requirementsCategorization';

const FullRequirementsReview = ({ vesselConfig }) => {
  const { currentVesselId } = useVessel();
  const [loading, setLoading] = useState(true);
  const [requirements, setRequirements] = useState(null);
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set(['equipment']));

  // Load all requirements
  useEffect(() => {
    const loadRequirements = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!vesselConfig) {
          throw new Error('Vessel configuration is required');
        }

        const reqs = getHardcodedVesselRequirements(vesselConfig);

        // Debug: Log requirement breakdown by original categories
        if (process.env.NODE_ENV === 'development') {

          Object.entries(reqs).forEach(([category, items]) => {
            if (Array.isArray(items) && items.length > 0) {

              items.forEach(item => {

              });
            }
          });
        }
        
        setRequirements(reqs);
        
      } catch (err) {

        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadRequirements();
  }, [vesselConfig]);

  // Process requirements into categories with smart filtering
  const processedCategories = useMemo(() => {
    if (!requirements) return {};
    
    // Collect all items from all fields
    const allItems = [];
    Object.values(requirements).forEach(fieldItems => {
      if (Array.isArray(fieldItems)) {
        allItems.push(...fieldItems);
      }
    });
    
    const categories = {};
    const categorizedItems = new Set(); // Track items to prevent duplicates
    
    // First pass: categorize using smart filters
    Object.entries(REQUIREMENT_CATEGORIES).forEach(([key, config]) => {
      const items = [];
      
      // Add items from designated fields
      config.fields.forEach(field => {
        const fieldItems = requirements[field] || [];
        items.push(...fieldItems);
        fieldItems.forEach(item => categorizedItems.add(item));
      });
      
      // Add items that match smart filter but aren't already categorized
      if (config.smartFilter) {
        allItems.forEach(item => {
          if (!categorizedItems.has(item) && config.smartFilter(item)) {
            items.push(item);
            categorizedItems.add(item);
          }
        });
      }
      
      // Remove duplicates
      const uniqueItems = items.filter((item, index, arr) => 
        arr.findIndex(i => i.id === item.id || (i.name === item.name && i.category === item.category)) === index
      );
      
      categories[key] = {
        ...config,
        items: uniqueItems,
        count: uniqueItems.length
      };
    });
    
    // Add uncategorized items to vessel documentation as fallback
    const uncategorizedItems = allItems.filter(item => !categorizedItems.has(item));
    if (uncategorizedItems.length > 0) {
      categories.vesselDocumentation.items.push(...uncategorizedItems);
      categories.vesselDocumentation.count = categories.vesselDocumentation.items.length;
    }
    
    // Debug: Log smart categorization results
    if (process.env.NODE_ENV === 'development') {

      Object.entries(categories).forEach(([key, category]) => {

        if (key === 'equipment' && category.count > 0) {

          category.items.forEach(item => {

          });
        }
        if (key === 'training' && category.count > 0) {

          category.items.forEach(item => {

          });
        }
      });
      if (uncategorizedItems.length > 0) {

        uncategorizedItems.forEach(item => {

        });
      }
    }
    
    return categories;
  }, [requirements]);

  // Calculate total requirements
  const totalRequirements = useMemo(() => {
    return Object.values(processedCategories).reduce((sum, category) => sum + category.count, 0);
  }, [processedCategories]);

  // Toggle category expansion
  const toggleCategory = (categoryKey) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryKey)) {
      newExpanded.delete(categoryKey);
    } else {
      newExpanded.add(categoryKey);
    }
    setExpandedCategories(newExpanded);
  };

  // Export requirements
  const exportRequirements = () => {
    let summary = `Vessel Requirements - Complete Review\n`;
    summary += `Generated: ${new Date().toLocaleDateString()}\n`;
    summary += `Vessel ID: ${currentVesselId}\n`;
    summary += `Vessel Type: ${vesselConfig.vesselType || 'Not specified'}\n`;
    summary += `Certificate: ${vesselConfig.baseCertificate || 'Standard Workboat'}\n`;
    summary += `Area Category: ${vesselConfig.category || 'Not specified'}\n`;
    summary += `Operating Status: ${vesselConfig.operatingStatus || 'seagoing'}\n\n`;
    summary += `Total Requirements: ${totalRequirements}\n\n`;
    
    Object.entries(processedCategories).forEach(([key, category]) => {
      if (category.items.length > 0) {
        summary += `${category.title} (${category.items.length} items):\n`;
        summary += `${'-'.repeat(50)}\n`;
        category.items.forEach(item => {
          summary += `• ${item.name}\n`;
          if (item.description) {
            summary += `  Description: ${item.description}\n`;
          }
          summary += `  Reference: ${item.reference || 'N/A'}\n`;
          summary += `  Category: ${item.category || 'General'}\n`;
          summary += `  Mandatory: ${item.mandatory !== false ? 'Yes' : 'No'}\n\n`;
        });
        summary += '\n';
      }
    });
    
    // Download as text file
    const blob = new Blob([summary], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vessel-requirements-complete-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading requirements...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
          <span className="text-red-800">Error loading requirements: {error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-cyan-900">Complete Requirements Review</h2>
            <p className="mt-1 text-sm text-cyan-700">
              All regulatory requirements for {vesselConfig.vesselType || 'this vessel'} - {totalRequirements} total requirements identified
            </p>
            <p className="mt-1 text-xs text-cyan-600">
              Certificate: {vesselConfig.baseCertificate || 'Standard Workboat'} | 
              Area Category: {vesselConfig.category} | 
              Status: {vesselConfig.operatingStatus || 'seagoing'}
            </p>
          </div>
          <button
            onClick={exportRequirements}
            className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
          >
            <Download className="h-4 w-4" />
            Export All
          </button>
        </div>
      </div>

      {/* Category Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {Object.entries(processedCategories).map(([key, category]) => {
          const Icon = category.icon;
          return (
            <div 
              key={key}
              className={`p-3 border rounded-lg text-center cursor-pointer transition-colors ${
                expandedCategories.has(key)
                  ? `bg-${category.color}-50 border-${category.color}-200` 
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
              }`}
              onClick={() => toggleCategory(key)}
            >
              <Icon className={`h-5 w-5 mx-auto mb-1 ${
                expandedCategories.has(key) ? `text-${category.color}-600` : 'text-gray-500'
              }`} />
              <div className="text-xs font-medium text-gray-900">{category.count}</div>
              <div className="text-xs text-gray-600 truncate">{category.title.replace(/🔧|👥|🎓|📋|📄|⚠️|🚫/g, '').trim()}</div>
            </div>
          );
        })}
      </div>

      {/* Requirements Categories */}
      {Object.entries(processedCategories).map(([key, category]) => {
        const Icon = category.icon;
        const isExpanded = expandedCategories.has(key);
        
        return (
          <div key={key} className="border border-gray-200 rounded-lg overflow-hidden">
            {/* Category Header */}
            <button
              onClick={() => toggleCategory(key)}
              className={`w-full p-4 text-left flex items-center justify-between transition-colors ${
                isExpanded 
                  ? `bg-${category.color}-50 border-b border-${category.color}-200` 
                  : 'bg-gray-50 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center gap-3">
                <Icon className={`h-5 w-5 ${
                  isExpanded ? `text-${category.color}-600` : 'text-gray-500'
                }`} />
                <div>
                  <h3 className="text-base font-medium text-gray-900">{category.title}</h3>
                  <p className="text-xs text-gray-600">{category.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  category.count > 0 
                    ? `bg-${category.color}-100 text-${category.color}-800`
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {category.count} items
                </span>
                <Eye className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''} text-gray-400`} />
              </div>
            </button>

            {/* Category Content */}
            {isExpanded && (
              <div className="p-4 space-y-3">
                {category.items.length > 0 ? (
                  category.items.map((item, index) => (
                    <div 
                      key={item.id || `${key}-${index}`}
                      className={`border rounded-lg p-3 ${
                        item.mandatory !== false 
                          ? `border-${category.color}-200 bg-${category.color}-25`
                          : 'border-gray-200 bg-gray-25'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-start gap-2">
                            <h4 className="text-sm font-medium text-gray-900">
                              {item.name}
                            </h4>
                            {item.mandatory !== false && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                Mandatory
                              </span>
                            )}
                          </div>
                          {item.description && (
                            <p className="mt-1 text-xs text-gray-600">{item.description}</p>
                          )}
                          {item.note && (
                            <p className="mt-1 text-xs text-gray-500 italic">{item.note}</p>
                          )}
                          {item.items && (
                            <ul className="mt-2 text-xs text-gray-600 list-disc list-inside">
                              {item.items.map((subItem, idx) => (
                                <li key={idx}>{subItem}</li>
                              ))}
                            </ul>
                          )}
                          <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
                            <span>📋 {item.category || 'General'}</span>
                            <span>📖 {item.reference || 'N/A'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 italic text-center py-4">
                    No {category.title.toLowerCase()} required for this vessel configuration
                  </p>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Footer */}
      <div className="pt-6 border-t border-gray-200 text-center">
        <p className="text-sm text-gray-600">
          Requirements generated from vessel configuration • {new Date().toLocaleDateString()}
        </p>
        <p className="text-xs text-gray-500 mt-1">
          This list shows all regulatory requirements based on your vessel's certificate, area category, and operational configuration
        </p>
      </div>
    </div>
  );
};

export default FullRequirementsReview;